// Iter 98 · Panel "Mi día" — agregado de pendientes operativas del usuario
// para el dashboard. Se monta opcionalmente arriba del grupo de categorías.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, AlertOctagon, BadgeAlert, Clock, HardHat, Megaphone, Sun,
  ChevronRight, CheckCircle2, ListChecks,
} from "lucide-react";
import api from "@/lib/api";

const ICONS = {
  AlertOctagon, BadgeAlert, Clock, HardHat, Megaphone, ListChecks,
};

function eur(n) {
  if (typeof n !== "number" || n === 0) return null;
  return n.toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
}

export default function MiDiaPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get("/dashboard/mi-dia");
        if (alive) setData(data);
      } catch {
        if (alive) setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="rounded-2xl border border-[var(--legatum-border)] bg-[var(--legatum-surface)] p-4 mb-8 flex items-center gap-3">
        <Loader2 className="h-5 w-5 animate-spin text-[#7C3AED]" />
        <span className="text-xs text-[var(--legatum-text-muted)]">Cargando tu día…</span>
      </div>
    );
  }
  if (!data) return null;

  const bloquesConItems = (data.bloques || []).filter((b) => (b.items || []).length > 0);
  if (data.total_pendientes === 0) {
    return (
      <div className="rounded-2xl border border-[#0F8A5F]/30 bg-[#0F8A5F]/5 p-4 mb-8 flex items-center gap-3" data-testid="mi-dia-vacio">
        <div className="w-10 h-10 rounded-xl bg-[#0F8A5F]/20 flex items-center justify-center">
          <CheckCircle2 className="h-5 w-5 text-[#0F8A5F]" />
        </div>
        <div>
          <div className="text-sm font-bold text-[#0F8A5F]">Tu día está al día</div>
          <div className="text-[11px] text-[var(--legatum-text-muted)]">Sin pendientes críticos hoy · {data.fecha}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-[#7C3AED]/30 bg-[var(--legatum-surface)] mb-8 overflow-hidden" data-testid="mi-dia-panel">
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center gap-3 p-4 border-b border-[var(--legatum-border)] hover:bg-[#7C3AED]/5 transition-colors"
        data-testid="mi-dia-toggle"
      >
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#1E5BCC] flex items-center justify-center shrink-0">
          <Sun className="h-5 w-5 text-white" />
        </div>
        <div className="flex-1 text-left min-w-0">
          <div className="text-[10px] uppercase font-bold tracking-[0.22em] text-[#7C3AED]">Mi día</div>
          <div className="text-sm font-bold">
            {data.total_pendientes} pendiente{data.total_pendientes === 1 ? "" : "s"} para hoy
          </div>
        </div>
        <ChevronRight className={`h-4 w-4 text-[var(--legatum-text-muted)] transition-transform ${collapsed ? "" : "rotate-90"}`} />
      </button>

      {!collapsed && (
        <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="mi-dia-bloques">
          {bloquesConItems.map((b) => {
            const Icon = ICONS[b.icon] || AlertOctagon;
            return (
              <div
                key={b.key}
                className="rounded-xl border p-3"
                style={{
                  borderColor: `${b.color}55`,
                  background: `${b.color}08`,
                }}
                data-testid={`mi-dia-bloque-${b.key}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: `${b.color}22`, color: b.color }}
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </div>
                  <span
                    className="text-[10px] uppercase font-bold tracking-widest truncate"
                    style={{ color: b.color }}
                  >
                    {b.label}
                  </span>
                  <span className="ml-auto text-[10px] font-mono text-[var(--legatum-text-muted)]">
                    {b.items.length}
                  </span>
                </div>
                <ul className="space-y-1.5">
                  {b.items.slice(0, 4).map((it) => (
                    <li key={it.id}>
                      <Link
                        to={it.url || "#"}
                        className="block text-xs hover:bg-[var(--legatum-elevated)] rounded p-1.5 transition-colors"
                        data-testid={`mi-dia-item-${b.key}-${it.id}`}
                      >
                        <div className="font-medium truncate">{it.titulo}</div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[10px] text-[var(--legatum-text-muted)] truncate flex-1">{it.subtitulo}</span>
                          {eur(it.importe) && (
                            <span className="text-[10px] font-mono font-bold" style={{ color: b.color }}>
                              {eur(it.importe)}
                            </span>
                          )}
                        </div>
                      </Link>
                    </li>
                  ))}
                  {b.items.length > 4 && (
                    <li className="text-[10px] italic text-[var(--legatum-text-muted)] text-center pt-1">
                      +{b.items.length - 4} más…
                    </li>
                  )}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
